import { useState, useEffect } from 'react';
import { XMarkIcon, SparklesIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface Player {
  id: string;
  first_name: string;
  last_name: string;
  category: string;
  handicap: number;
  resident_club: string;
}

interface RankedPlayer extends Player {
  rank: number;
  total_score: number;
  rounds_played: number;
}

export type StartingTeeMode = 'hole1' | 'hole10' | 'split';

interface AutoPairConfig {
  categories: string[];
  hcpMin: number;
  hcpMax: number;
  groupSize: number;
  startTeeTime: string;
  teeTimeInterval: number;
  mode: 'random' | 'qualifier';
  qualifierPairingStyle: 'sequential' | 'seeded';
  startingTeeMode: StartingTeeMode;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  players: Player[];
  rankedPlayers?: RankedPlayer[];
  onGenerate: (config: AutoPairConfig) => void;
  generating: boolean;
  hasQualifyingRound?: boolean;
}

export default function AutoPairModal({ isOpen, onClose, players, rankedPlayers, onGenerate, generating, hasQualifyingRound }: Props) {
  const allCategories = [...new Set(players.map((p) => p.category).filter(Boolean))].sort();
  const minHcp = players.length > 0 ? Math.min(...players.map((p) => p.handicap)) : 0;
  const maxHcp = players.length > 0 ? Math.max(...players.map((p) => p.handicap)) : 36;

  const [mode, setMode] = useState<'random' | 'qualifier'>('random');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [hcpMin, setHcpMin] = useState(minHcp);
  const [hcpMax, setHcpMax] = useState(maxHcp);
  const [groupSize, setGroupSize] = useState(4);
  const [startTeeTime, setStartTeeTime] = useState('07:00');
  const [teeTimeInterval, setTeeTimeInterval] = useState(10);
  const [qualifierPairingStyle, setQualifierPairingStyle] = useState<'sequential' | 'seeded'>('sequential');
  const [startingTeeMode, setStartingTeeMode] = useState<StartingTeeMode>('hole1');

  useEffect(() => {
    if (isOpen) {
      setSelectedCategories(allCategories);
      setHcpMin(minHcp);
      setHcpMax(maxHcp);
      if (hasQualifyingRound) setMode('qualifier');
      else setMode('random');
    }
  }, [isOpen]);

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const eligible = mode === 'qualifier'
    ? (rankedPlayers ?? []).filter(
        (p) =>
          (selectedCategories.length === 0 || selectedCategories.includes(p.category)) &&
          p.handicap >= hcpMin &&
          p.handicap <= hcpMax
      )
    : players.filter(
        (p) =>
          (selectedCategories.length === 0 || selectedCategories.includes(p.category)) &&
          p.handicap >= hcpMin &&
          p.handicap <= hcpMax
      );

  const groupCount = Math.ceil(eligible.length / groupSize);

  const getQualifierPreview = () => {
    if (mode !== 'qualifier' || eligible.length === 0) return null;
    const ranked = eligible as RankedPlayer[];
    if (qualifierPairingStyle === 'seeded') {
      const pairs: [RankedPlayer, RankedPlayer | undefined][] = [];
      const n = ranked.length;
      for (let i = 0; i < Math.floor(n / 2); i++) {
        pairs.push([ranked[i], ranked[n - 1 - i]]);
      }
      if (n % 2 !== 0) pairs.push([ranked[Math.floor(n / 2)], undefined]);
      return pairs.slice(0, 3);
    } else {
      const pairs: [RankedPlayer, RankedPlayer | undefined][] = [];
      for (let i = 0; i < ranked.length; i += groupSize) {
        const group = ranked.slice(i, i + groupSize);
        pairs.push([group[0], group[1]]);
      }
      return pairs.slice(0, 3);
    }
  };

  const qualifierPreview = getQualifierPreview();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-golf-orange" />
            <h2 className="text-base font-bold text-golf-gray">Auto-Pair Players</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <XMarkIcon className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {hasQualifyingRound && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Pairing Mode
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setMode('random')}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    mode === 'random'
                      ? 'bg-golf-orange text-white border-golf-orange'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                  }`}
                >
                  <SparklesIcon className="w-4 h-4 flex-shrink-0" />
                  Random
                </button>
                <button
                  onClick={() => setMode('qualifier')}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors ${
                    mode === 'qualifier'
                      ? 'bg-golf-orange text-white border-golf-orange'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                  }`}
                >
                  <TrophyIcon className="w-4 h-4 flex-shrink-0" />
                  By Qualifier
                </button>
              </div>
              {mode === 'qualifier' && (
                <p className="text-xs text-gray-400 mt-2">
                  Players are ranked by their qualifying round scores and paired in order.
                </p>
              )}
            </div>
          )}

          {mode === 'qualifier' && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Pairing Style
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => setQualifierPairingStyle('sequential')}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors text-left ${
                    qualifierPairingStyle === 'sequential'
                      ? 'bg-golf-orange text-white border-golf-orange'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                  }`}
                >
                  <span className="block font-semibold">Sequential</span>
                  <span className={`text-xs ${qualifierPairingStyle === 'sequential' ? 'text-white/80' : 'text-gray-400'}`}>1st+2nd, 3rd+4th…</span>
                </button>
                <button
                  onClick={() => setQualifierPairingStyle('seeded')}
                  className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors text-left ${
                    qualifierPairingStyle === 'seeded'
                      ? 'bg-golf-orange text-white border-golf-orange'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                  }`}
                >
                  <span className="block font-semibold">Seeded</span>
                  <span className={`text-xs ${qualifierPairingStyle === 'seeded' ? 'text-white/80' : 'text-gray-400'}`}>1st vs last, 2nd vs 2nd-last…</span>
                </button>
              </div>
            </div>
          )}

          {allCategories.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Categories
              </label>
              <div className="flex flex-wrap gap-2">
                {allCategories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      selectedCategories.includes(cat)
                        ? 'bg-golf-orange text-white border-golf-orange'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-golf-orange hover:text-golf-orange'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Handicap Range
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-1 block">Min HCP</label>
                <input
                  type="number"
                  min={0}
                  max={36}
                  value={hcpMin}
                  onChange={(e) => setHcpMin(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange"
                />
              </div>
              <span className="text-gray-300 mt-4">—</span>
              <div className="flex-1">
                <label className="text-xs text-gray-400 mb-1 block">Max HCP</label>
                <input
                  type="number"
                  min={0}
                  max={36}
                  value={hcpMax}
                  onChange={(e) => setHcpMax(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Players per Group
            </label>
            <div className="flex gap-2">
              {[2, 3, 4].map((n) => (
                <button
                  key={n}
                  onClick={() => setGroupSize(n)}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${
                    groupSize === n
                      ? 'bg-golf-orange text-white border-golf-orange'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Start Tee Time
              </label>
              <input
                type="time"
                value={startTeeTime}
                onChange={(e) => setStartTeeTime(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Interval (min)
              </label>
              <input
                type="number"
                min={5}
                max={30}
                value={teeTimeInterval}
                onChange={(e) => setTeeTimeInterval(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-golf-orange"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
              Starting Tee
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setStartingTeeMode('hole1')}
                className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors text-center ${
                  startingTeeMode === 'hole1'
                    ? 'bg-golf-orange text-white border-golf-orange'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                }`}
              >
                <span className="block font-semibold">Hole 1</span>
                <span className={`text-xs ${startingTeeMode === 'hole1' ? 'text-white/80' : 'text-gray-400'}`}>Front 9</span>
              </button>
              <button
                onClick={() => setStartingTeeMode('hole10')}
                className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors text-center ${
                  startingTeeMode === 'hole10'
                    ? 'bg-golf-orange text-white border-golf-orange'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                }`}
              >
                <span className="block font-semibold">Hole 10</span>
                <span className={`text-xs ${startingTeeMode === 'hole10' ? 'text-white/80' : 'text-gray-400'}`}>Back 9</span>
              </button>
              <button
                onClick={() => setStartingTeeMode('split')}
                className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-colors text-center ${
                  startingTeeMode === 'split'
                    ? 'bg-golf-orange text-white border-golf-orange'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-golf-orange'
                }`}
              >
                <span className="block font-semibold">Split</span>
                <span className={`text-xs ${startingTeeMode === 'split' ? 'text-white/80' : 'text-gray-400'}`}>1 & 10</span>
              </button>
            </div>
            {startingTeeMode === 'split' && (
              <p className="text-xs text-gray-400 mt-2">
                First half of groups start from Hole 1, second half from Hole 10 simultaneously.
              </p>
            )}
          </div>

          {mode === 'qualifier' && qualifierPreview && qualifierPreview.length > 0 && (
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Preview (first {Math.min(3, qualifierPreview.length)} groups)
              </label>
              <div className="space-y-1.5">
                {qualifierPreview.map(([a, b], i) => (
                  <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                    <span className="text-xs font-bold text-golf-orange w-5 text-center">{i + 1}</span>
                    <span className="text-xs text-golf-gray font-medium flex-1 truncate">
                      {a.first_name} {a.last_name}
                      <span className="text-gray-400 font-normal"> #{a.rank}</span>
                    </span>
                    {b && (
                      <>
                        <span className="text-gray-300 text-xs">vs</span>
                        <span className="text-xs text-golf-gray font-medium flex-1 truncate text-right">
                          <span className="text-gray-400 font-normal">#{b.rank} </span>
                          {b.first_name} {b.last_name}
                        </span>
                      </>
                    )}
                  </div>
                ))}
                {groupCount > 3 && (
                  <p className="text-xs text-gray-400 text-center">…and {groupCount - 3} more groups</p>
                )}
              </div>
            </div>
          )}

          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-golf-gray">{eligible.length}</span> eligible players will be{' '}
              {mode === 'qualifier' ? 'paired by qualifier ranking into' : 'randomly distributed into'}{' '}
              <span className="font-semibold text-golf-gray">{groupCount}</span> group{groupCount !== 1 ? 's' : ''} of up to{' '}
              <span className="font-semibold text-golf-gray">{groupSize}</span>.
              {startingTeeMode === 'split' && groupCount > 1 && (
                <span className="block mt-1 text-xs text-gray-500">
                  {Math.ceil(groupCount / 2)} group{Math.ceil(groupCount / 2) !== 1 ? 's' : ''} from Hole 1, {Math.floor(groupCount / 2)} from Hole 10.
                </span>
              )}
            </p>
            {eligible.length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                {mode === 'qualifier' && (!rankedPlayers || rankedPlayers.length === 0)
                  ? 'No qualifying round scores found. Record scores for a previous round first.'
                  : 'No players match the selected criteria.'}
              </p>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() =>
              onGenerate({ categories: selectedCategories, hcpMin, hcpMax, groupSize, startTeeTime, teeTimeInterval, mode, qualifierPairingStyle, startingTeeMode })
            }
            disabled={eligible.length === 0 || generating}
            className="flex-1 py-2.5 rounded-xl bg-golf-orange text-white text-sm font-semibold hover:bg-golf-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {generating ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                Generating...
              </span>
            ) : (
              <>
                {mode === 'qualifier' ? <TrophyIcon className="w-4 h-4" /> : <SparklesIcon className="w-4 h-4" />}
                Generate Pairings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
